<!-- Toolbar -->
<div class="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
  <div>
    <p class="text-theme-sm text-gray-500 dark:text-gray-400">
      @if (totalCount > 0) {
        {{ totalCount }} servicio{{ totalCount !== 1 ? 's' : '' }}@if (priceRange) {<span>, precios de </span><span class="font-medium text-gray-800 tabular-nums dark:text-white/90">{{ priceRange }}</span>}
      }
    </p>
  </div>
  <div class="flex items-center gap-2">
    <div class="relative flex-1 sm:flex-none">
      <svg class="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" fill="none"
        stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input type="search" [value]="searchTerm" (input)="onSearch($any($event.target).value)"
        placeholder="Buscar servicio"
        aria-label="Buscar servicio"
        class="sv-input h-10 w-full pl-9 pr-3 sm:w-64" />
    </div>
    <button *appHasAny="['services.create']" type="button" (click)="onCreateService()"
      class="sv-btn sv-btn--primary inline-flex shrink-0 items-center gap-2"
      aria-label="Crear servicio">
      <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
      <span class="hidden sm:inline">Crear servicio</span>
    </button>
  </div>
</div>

<!-- Unstaffed services notice -->
@if (unstaffedCount > 0) {
  <div class="mb-4 flex gap-3 rounded-xl border border-warning-200 bg-warning-50 px-4 py-3 text-theme-sm text-warning-700
    dark:border-warning-500/30 dark:bg-warning-500/10 dark:text-warning-200" role="status">
    <svg class="mt-0.5 size-5 shrink-0 text-warning-500" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
    </svg>
    <p>
      @if (unstaffedCount === 1) {
        Un servicio no tiene profesionales asignados y nadie puede atenderlo.
      } @else {
        {{ unstaffedCount }} servicios no tienen profesionales asignados y nadie puede atenderlos.
      }
      @if (canEdit) {
        Edítalos para elegir quién los ofrece.
      }
    </p>
  </div>
}

<!-- Skeleton loader -->
@if (loading && services.length === 0) {
  <div class="sv-list" role="status" aria-label="Cargando servicios">
    @for (item of skeletonRows; track $index) {
      <div class="sv-row animate-pulse">
        <div class="sv-thumb bg-gray-100 dark:bg-gray-800" style="grid-area: thumb"></div>
        <div style="grid-area: info">
          <div class="mb-2 h-4 w-40 rounded bg-gray-200 dark:bg-gray-800"></div>
          <div class="h-3 w-56 max-w-full rounded bg-gray-100 dark:bg-gray-800/70"></div>
        </div>
        <div class="h-2 w-full rounded bg-gray-100 dark:bg-gray-800/70" style="grid-area: time"></div>
        <div class="ml-auto h-4 w-20 rounded bg-gray-200 dark:bg-gray-800" style="grid-area: price"></div>
      </div>
    }
  </div>
}

<!-- Empty state -->
@if (!loading && services.length === 0) {
  <div class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-16 text-center dark:border-gray-700 dark:bg-white/[0.03]">
    @if (searchTerm) {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">Ningún servicio coincide con "{{ searchTerm }}"</h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Revisa la ortografía o busca por otra palabra del nombre.
      </p>
      <button type="button" (click)="onSearch(''); searchTerm = ''" class="sv-btn sv-btn--ghost">
        Limpiar búsqueda
      </button>
    } @else {
      <h3 class="mb-1 text-base font-semibold text-gray-800 dark:text-white/90">Aún no tienes servicios</h3>
      <p class="mb-5 max-w-sm text-theme-sm text-gray-500 dark:text-gray-400">
        Agrega lo que ofreces, con su duración y precio, para que tus clientes puedan reservar.
      </p>
      <button *appHasAny="['services.create']" type="button" (click)="onCreateService()"
        class="sv-btn sv-btn--primary inline-flex items-center gap-2">
        <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        Crear tu primer servicio
      </button>
    }
  </div>
}

<!-- Service menu -->
@if (services.length > 0) {
  <div class="sv-list" [class.opacity-60]="loading">
    <div class="sv-row sv-row--head" aria-hidden="true">
      <span style="grid-area: info">Servicio</span>
      <span style="grid-area: time">Duración</span>
      <span style="grid-area: team">Equipo</span>
      <span class="text-right" style="grid-area: price">Precio</span>
    </div>

    <ul>
      @for (service of services; track service.serviceId) {
        @let team = serviceProfessionalsMap()[service.serviceId] ?? [];
        <li class="sv-row">
          <!-- Thumbnail -->
          <div class="sv-thumb" style="grid-area: thumb">
            @if (service.imageUrl) {
              <img [src]="service.imageUrl" alt="" class="h-full w-full object-cover" loading="lazy" />
            } @else {
              <span aria-hidden="true">{{ (service.name[0] || '?') | uppercase }}</span>
            }
          </div>

          <!-- Name & description -->
          <div class="min-w-0" style="grid-area: info">
            <h3 class="sv-name truncate">{{ service.name }}</h3>
            @if (service.description) {
              <p class="sv-desc line-clamp-1">{{ service.description }}</p>
            }
            @if (service.hasSessions && service.sessionsCount) {
              <p class="sv-sessions">
                Paquete de {{ service.sessionsCount }} sesiones@if (service.sessionIntervalDays) {<span>, cada {{ service.sessionIntervalDays }} día{{ service.sessionIntervalDays !== 1 ? 's' : '' }}</span>}
              </p>
            }
          </div>

          <!-- Duration -->
          <div class="sv-time" style="grid-area: time">
            <span class="sv-time__label">{{ service.durationLabel || getDurationLabel(service.durationMinutes) }}</span>
            <span class="sv-time__track" aria-hidden="true">
              <span class="sv-time__bar" [style.width.%]="durationPercent(service.durationMinutes)"></span>
            </span>
          </div>

          <!-- Team -->
          <div class="sv-team" style="grid-area: team">
            @if (team.length) {
              <div class="flex -space-x-1.5" [attr.aria-label]="team.length + ' profesional' + (team.length !== 1 ? 'es' : '')" role="img">
                @for (prof of team.slice(0, 3); track prof.userId) {
                  <span class="sv-avatar" [style.background-color]="prof.color || 'var(--color-brand-500)'"
                    [title]="professionalName(prof)">
                    @if (prof.avatarUrl) {
                      <img [src]="prof.avatarUrl" alt="" class="h-full w-full object-cover" loading="lazy" />
                    } @else {
                      {{ (prof.firstName?.[0] || '?') | uppercase }}
                    }
                  </span>
                }
                @if (team.length > 3) {
                  <span class="sv-avatar sv-avatar--more">+{{ team.length - 3 }}</span>
                }
              </div>
            } @else if (professionalsLoaded()) {
              <span class="sv-team__empty">Sin asignar</span>
            }
          </div>

          <!-- Price -->
          <p class="sv-price" style="grid-area: price">{{ formatPrice(service.price) }}</p>

          <!-- Actions -->
          @if (canEdit || canDelete) {
            <div class="flex items-center justify-end gap-1" style="grid-area: actions">
              @if (canEdit) {
                <button type="button" class="sv-icon-btn" (click)="onEditService(service.serviceId)"
                  [attr.aria-label]="'Editar ' + service.name" title="Editar">
                  <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
                  </svg>
                </button>
              }
              @if (canDelete) {
                <button type="button" class="sv-icon-btn sv-icon-btn--danger" (click)="openDelete(service)"
                  [attr.aria-label]="'Eliminar ' + service.name" title="Eliminar">
                  <svg class="size-4" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                  </svg>
                </button>
              }
            </div>
          }
        </li>
      }
    </ul>
  </div>

  <!-- Pagination -->
  @if (totalPages > 1) {
    <nav class="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row" aria-label="Paginación">
      <p class="text-theme-sm text-gray-500 tabular-nums dark:text-gray-400">
        {{ (page - 1) * pageSize + 1 }}–{{ page * pageSize > totalCount ? totalCount : page * pageSize }} de {{ totalCount }}
      </p>
      <div class="flex items-center gap-1">
        <button type="button" (click)="onPageChange(page - 1)" [disabled]="page <= 1" class="sv-page" aria-label="Página anterior">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
        </button>
        @for (p of [].constructor(totalPages); track $index) {
          @if ($index + 1 === page || $index + 1 === 1 || $index + 1 === totalPages || ($index + 1 >= page - 1 && $index + 1 <= page + 1)) {
            <button type="button" (click)="onPageChange($index + 1)" class="sv-page"
              [attr.aria-current]="($index + 1) === page ? 'page' : null">
              {{ $index + 1 }}
            </button>
          } @else if ($index + 1 === page - 2 || $index + 1 === page + 2) {
            <span class="inline-flex size-9 items-center justify-center text-sm text-gray-400" aria-hidden="true">…</span>
          }
        }
        <button type="button" (click)="onPageChange(page + 1)" [disabled]="page >= totalPages" class="sv-page" aria-label="Página siguiente">
          <svg class="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
        </button>
      </div>
    </nav>
  }
}

<!-- Create/Edit Modal -->
<app-modal [open]="open" [title]="modalTitle" [size]="'md'" [bodyComponent]="ServiceAddForm" [closeOnBackdrop]="false"
  [bodyInputs]="modalInputs" (confirm)="onModalConfirm($event)" (close)="open = false" [isSaving]="isSaving">
</app-modal>

<!-- Delete Confirmation Modal -->
<app-confirm-modal [(open)]="confirmOpen"
  title="Eliminar servicio"
  [subtitle]="toDeleteName ? 'Estás a punto de eliminar el servicio \'' + toDeleteName + '\'. Esta acción no se puede deshacer.' : 'Esta acción no se puede deshacer, ¿deseas continuar?'"
  cancelText="No, volver" confirmText="Sí, eliminar"
  variant="danger" [closeOnBackdrop]="true" (confirm)="onConfirmDelete()" (cancel)="toDeleteId = null; toDeleteName = null">
</app-confirm-modal>
